import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const Field = ({ label, name, type = 'text', value, onChange, required, className = '' }) => (
  <div className={className}>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}{required && ' *'}</label>
    <input type={type} name={name} value={value} onChange={onChange} required={required}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
  </div>
);

const FileField = ({ label, name, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input type="file" name={name} onChange={onChange} accept=".jpg,.jpeg,.png,.pdf"
      className="w-full text-sm text-gray-500 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100" />
  </div>
);

export default function StudentForm({ onClose, onSaved, initial }) {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({
    firstName: '', lastName: '', email: '', phone: '', nicNumber: '', dateOfBirth: '', address: '', university: '', course: '', academicYear: '',
    fatherName: '', fatherPhone: '', fatherNic: '', fatherOccupation: '',
    motherName: '', motherPhone: '', motherNic: '', motherOccupation: '',
    guardianName: '', guardianPhone: '', guardianNic: '', guardianRelation: '',
    emergencyContact: '', emergencyPhone: '',
    ...initial,
  });
  const [files, setFiles] = useState({});

  const set = e => setData(p => ({ ...p, [e.target.name]: e.target.value }));
  const setFile = e => setFiles(p => ({ ...p, [e.target.name]: e.target.files[0] }));

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(data).forEach(([k, v]) => { if (v) fd.append(k, v); });
      Object.entries(files).forEach(([k, v]) => { if (v) fd.append(k, v); });

      if (initial?.id) {
        await api.put(`/students/${initial.id}`, fd);
        toast.success('Student updated');
      } else {
        await api.post('/students', fd);
        toast.success('Student registered');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error saving student');
    } finally {
      setLoading(false);
    }
  };

  const tabs = ['Personal', 'Family', 'Guardian', 'Documents'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-bold text-lg">{initial ? 'Edit Student' : 'Register Student'}</h3>
          <button onClick={onClose}><X size={20} className="text-gray-500" /></button>
        </div>

        <div className="flex border-b px-5">
          {tabs.map((t, i) => (
            <button key={i} onClick={() => setTab(i)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors ${tab === i ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5">
          {tab === 0 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First Name" name="firstName" value={data.firstName} onChange={set} required />
                <Field label="Last Name" name="lastName" value={data.lastName} onChange={set} required />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Phone" name="phone" value={data.phone} onChange={set} required />
                <Field label="Email" name="email" type="email" value={data.email} onChange={set} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="NIC Number" name="nicNumber" value={data.nicNumber} onChange={set} />
                <Field label="Date of Birth" name="dateOfBirth" type="date" value={data.dateOfBirth} onChange={set} />
              </div>
              <Field label="Address" name="address" value={data.address} onChange={set} />
              <div className="grid grid-cols-3 gap-3">
                <Field label="University" name="university" value={data.university} onChange={set} />
                <Field label="Course" name="course" value={data.course} onChange={set} />
                <Field label="Academic Year" name="academicYear" value={data.academicYear} onChange={set} />
              </div>
            </div>
          )}

          {tab === 1 && (
            <div className="space-y-6">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Father's Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Full Name" name="fatherName" value={data.fatherName} onChange={set} />
                  <Field label="Phone" name="fatherPhone" value={data.fatherPhone} onChange={set} />
                  <Field label="NIC" name="fatherNic" value={data.fatherNic} onChange={set} />
                  <Field label="Occupation" name="fatherOccupation" value={data.fatherOccupation} onChange={set} />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Mother's Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Full Name" name="motherName" value={data.motherName} onChange={set} />
                  <Field label="Phone" name="motherPhone" value={data.motherPhone} onChange={set} />
                  <Field label="NIC" name="motherNic" value={data.motherNic} onChange={set} />
                  <Field label="Occupation" name="motherOccupation" value={data.motherOccupation} onChange={set} />
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Emergency Contact</h4>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Name" name="emergencyContact" value={data.emergencyContact} onChange={set} />
                  <Field label="Phone" name="emergencyPhone" value={data.emergencyPhone} onChange={set} />
                </div>
              </div>
            </div>
          )}

          {tab === 2 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-gray-700">Guardian Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Full Name" name="guardianName" value={data.guardianName} onChange={set} />
                <Field label="Phone" name="guardianPhone" value={data.guardianPhone} onChange={set} />
                <Field label="NIC" name="guardianNic" value={data.guardianNic} onChange={set} />
                <Field label="Relationship" name="guardianRelation" value={data.guardianRelation} onChange={set} />
              </div>
            </div>
          )}

          {tab === 3 && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Upload JPG, PNG or PDF (max 5MB each)</p>
              <FileField label="Student ID / Card" name="studentIdDoc" onChange={setFile} />
              <FileField label="Student NIC" name="nicDoc" onChange={setFile} />
              <FileField label="Father's NIC" name="fatherNicDoc" onChange={setFile} />
              <FileField label="Mother's NIC" name="motherNicDoc" onChange={setFile} />
              <FileField label="Guardian's NIC" name="guardianNicDoc" onChange={setFile} />
              <FileField label="Student Photo" name="photoDoc" onChange={setFile} />
            </div>
          )}

          <div className="flex gap-3 mt-6 pt-4 border-t">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm">Cancel</button>
            <button type="submit" disabled={loading} className="flex-1 bg-brand-700 hover:bg-brand-800 text-white py-2 rounded-lg text-sm disabled:opacity-60">
              {loading ? 'Saving...' : initial ? 'Update' : 'Register'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
